import { DialogService } from '@eforall/dashboard';
import { AdmissionsMatchingFuncData, AdmissionsMatchingVentureFuncData } from '@interfaces';
import { MatchedTeam, MatchedTeamVenture, MatchedTrimester } from './match-data-interfaces';


/**
 * Builds an initial set of match data.  Note that scoring must be
 * performed in the worker and passed back out with a posted message.
 */
export async function initializeMatchData(
	run: 'Test' | 'Live',
	data: AdmissionsMatchingFuncData,
	dialogService: DialogService
): Promise<{ matchData: MatchedTrimester, teamByVolunteerSignupMentorId: Map<number, MatchedTeam> }> {

	//
	// Create the outer trimester object
	//
	const t: MatchedTrimester = {
		trimesterId: data.trimesterId,
		canApply: false,
		accs: [],
		teamsWithoutAMeeting: 100,
		donorGeoScore: 0,
		donorGeoMentors: [],
		lowestTeamScorePct: 0,
	};



	//
	// Only use accs with ventures that have initially matched teams (overlapping schedules)
	//
	const accs = data.accelerators.filter(a => !!a.teams);

	if (run == 'Live' && accs.length !== data.accelerators.length) {
		//
		// A critical message should stop the Live Run button from being available,
		// so this scenario shouldn't happen.  However, just in case...
		//
		throw new Error(`Attempt to perform a Live Run but ${data.accelerators.length - accs.length} acc(s) don't have initial teams of schedule-compatible ventures.`);
	}


	//
	// A lookup to map the ids in the teams array to the venture data
	//
	const venturesByAccApplicationId = new Map<number, AdmissionsMatchingVentureFuncData>();

	for (const acc of accs) {
		for (const venture of acc.ventures) {
			venturesByAccApplicationId.set(venture.accApplicationId, venture);
		}
	}


	//
	// Create the accelerators (initially teams with ventures, but no mentors or scores)
	//
	t.accs = accs.map(a => ({
		accId: a.accId,
		accName: a.name,
		languageId: a.languageId,
		hasMentors: a.hasMentors,
		teams: a.teams!.map(accApplicationIds => ({
			people: {
				ventures: accApplicationIds.map(id => {
					const venture = venturesByAccApplicationId.get(id)!;	// We know there is a value because it is added to the Map above
					const data: MatchedTeamVenture = {
						accApplicationId: venture.accApplicationId,
						zipId: venture.zipId!,
						location: venture.location,
						applicantName: venture.applicantName,
						isFemale: venture.gender == 'F',
						meetings: venture.meetings!,
						businessStageNum: parseInt(venture.businessStage, 10),
						isNonProfit: venture.businessType == 'Non-Profit',
						industryId: venture.industryId,
					};

					return data;
				}),
				mentors: [],
				overlappingMeetings: 0,
				monToSun: [0, 0, 0, 0, 0, 0, 0],
				geoDonorsMiles: [],
				teamScore: 0,
				teamScorePct: 0,
				teamScoreValues: {
					nonprofits: 0,
					sameIndustry: 0,
					sameStage: 0,
					women: 0,
					ventureLocations: '',
					ventureDistances: '',

					mentorGeoDonor: '',
					mentorWoman: '',
					mentorLocations: '',
					mentorDistances: '',
				},
				matchingSummary: '',
			},
			attemptedSwap: undefined,
			displayColor: '',
			scoreSummary: '',
		})),
	}));


	//
	// Prepare Mentors
	//
	let mentors = [...data.mentors];
	const teamByVolunteerSignupMentorId = new Map<number, MatchedTeam>();

	if (run == 'Live') {
		//
		// Only consider mentors with status 'Assigned' during Live run
		//
		mentors = mentors.filter(m => m.status == 'Assigned');
	}

	for (const acc of t.accs) {

		if (!acc.hasMentors) continue;

		let languageMentors = mentors.filter(m => m.languageId == acc.languageId || m.languageId == undefined);

		if (languageMentors.length < 14) {
			if (run == 'Test') {
				await dialogService.showMessage('Test Run with Not Enough Mentors', `There are not enough mentors to fill accelerator ${acc.accName}.
			
This accelerator will be without mentors during this Test Run.`, 'I Understand', [], 600, 200);

				acc.hasMentors = false;
				continue;
			}
			else {
				// throw new Error(`Need 14 mentors for acc ${acc.accName} but only have ${languageMentors.length}.`);
				console.warn(`Not enough mentors to fill accelerator ${acc.accName} during Live Run. Accelerator will be without mentors.`);
				acc.hasMentors = false;
				continue;
			}
		}

		for (const team of acc.teams) {

			for (let i = 0; i <= 1; i++) { // Two mentors

				languageMentors = mentors.filter(m => m.languageId == acc.languageId || m.languageId == undefined);

				//
				// Randomly select a mentor
				//
				const selectedMentor = languageMentors[Math.floor(Math.random() * languageMentors.length)]!;

				//
				// Remove the mentor from the available list
				//
				mentors = mentors.filter(m => m.volunteerSignupMentorId !== selectedMentor.volunteerSignupMentorId)

				//
				// Add to team
				//
				team.people.mentors.push({
					volunteerSignupMentorId: selectedMentor.volunteerSignupMentorId,
					zipId: selectedMentor.zipId!,
					location: selectedMentor.location,
					name: selectedMentor.name,
					isFemale: selectedMentor.gender == 'F',
					status: selectedMentor.status,
					isDonorWithGeoMatchPriority: selectedMentor.isDonorWithGeoMatchPriority,
					milesToClosestEntrepreneur: 100,
					languageId: selectedMentor.languageId,
					meetings: selectedMentor.meetings!,
				});

				//
				// Remember the team for each mentor
				//
				teamByVolunteerSignupMentorId.set(selectedMentor.volunteerSignupMentorId, team);
			}
		}
	}

	if (mentors.length && run == 'Live') {
		throw new Error(`Expected all mentors to be used but ${mentors.length} were remaining after initial assignment`);
	}

	return { matchData: t, teamByVolunteerSignupMentorId };
}